/**
 * Check Email Page
 *
 * Displayed after successful registration.
 * Includes resend email functionality with cooldown.
 */

import * as React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@provenleap/ui";
import { MailIcon } from "../../components/icons";

interface CheckEmailPageProps {
  email?: string;
  apiBaseUrl?: string;
}

export function CheckEmailPage({
  email = "your email",
  apiBaseUrl = "",
}: CheckEmailPageProps) {
  const [isResending, setIsResending] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [message, setMessage] = React.useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Handle cooldown timer
  React.useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [cooldown]);

  const handleResendEmail = async (): Promise<void> => {
    if (cooldown > 0) return;

    setIsResending(true);
    setMessage(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setMessage({ type: "success", text: "Verification email sent!" });
        setCooldown(60); // 60 second cooldown
      } else {
        const data = await response.json();
        setMessage({
          type: "error",
          text: data.detail || "Failed to resend email. Please try again.",
        });
      }
    } catch {
      setMessage({ type: "error", text: "Network error. Please try again." });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Card className="w-full text-center !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <MailIcon
          className="mx-auto mb-4 text-foreground"
          size={30}
        />
        <CardTitle className="mb-2">Check Your Email</CardTitle>
        <CardDescription className="text-neutral-700">
          We've sent a verification link to your email address
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-neutral-700">
          Click the link in the email to verify your account. The link will
          expire in 24 hours.
        </p>

        {/* Message feedback */}
        {message && (
          <div
            className={`rounded-md p-3 text-sm ${
              message.type === "success"
                ? "bg-success/10 text-success"
                : "bg-error/10 text-error"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Tips */}
        <div className="text-left bg-white rounded-md border border-border p-4">
          <p className="text-sm font-medium mb-2">Didn't receive the email?</p>
          <ul className="text-[14px] text-neutral-700 space-y-1">
            <li>• Make sure you entered the correct email</li>
            <li>• Wait a few minutes and try again</li>
          </ul>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <Button
          className="w-full cursor-pointer"
          onClick={handleResendEmail}
          disabled={isResending || cooldown > 0}
        >
          {isResending ? (
            "Sending..."
          ) : cooldown > 0 ? (
            `Resend in ${cooldown}s`
          ) : (
            "Resend Verification Email"
          )}
        </Button>
        <a
          href="/login"
          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
        >
          <span>←</span> Back to Login
        </a>
      </CardFooter>
    </Card>
  );
}
